import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";

// Generate slug from name
const slugify = (text) => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/[^a-z0-9]+/g, "-")     // Replace non-alphanumeric with -
    .replace(/^-+|-+$/g, "");        // Trim leading/trailing -
};

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({ slug: "", name: "", display_order: 0 });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getCategories();
      setCategories(data.data);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleCreate = async () => {
    if (!form.name) {
      toast.error("Name is required");
      return;
    }
    try {
      await adminApi.createCategory(form);
      toast.success("Category created");
      setShowCreate(false);
      setForm({ slug: "", name: "", display_order: 0 });
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleUpdate = async () => {
    if (!editingCategory) return;
    try {
      await adminApi.updateCategory(editingCategory.id, {
        name: form.name,
        display_order: form.display_order,
        is_active: form.is_active
      });
      toast.success("Category updated");
      setEditingCategory(null);
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this category?")) return;
    try {
      await adminApi.deleteCategory(id);
      toast.success("Category deactivated");
      fetchCategories();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openEdit = (category) => {
    setForm({
      name: category.name,
      display_order: category.display_order,
      is_active: category.is_active
    });
    setEditingCategory(category);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Categories ({categories.length})</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button>Add Category</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Category</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  placeholder="e.g. Apparel"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value, slug: slugify(e.target.value)})}
                />
                {form.slug && (
                  <p className="text-xs text-gray-500 mt-1">Slug: <code>{form.slug}</code></p>
                )}
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({...form, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
              <Button onClick={handleCreate} className="w-full">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingCategory} onOpenChange={(open) => !open && setEditingCategory(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Category: {editingCategory?.slug}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({...form, display_order: parseInt(e.target.value) || 0})}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({...form, is_active: v})}
              />
              <Label>Active</Label>
            </div>
            <Button onClick={handleUpdate} className="w-full">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Order</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id} className={!category.is_active ? "opacity-50" : ""}>
                <TableCell>{category.display_order}</TableCell>
                <TableCell className="font-mono text-sm">{category.slug}</TableCell>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell>
                  <Badge variant={category.is_active ? "default" : "destructive"}>
                    {category.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(category)}>
                      Edit
                    </Button>
                    {category.is_active && (
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(category.id)}>
                        Deactivate
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                  No categories yet. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
